import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  UserPlus,
  Search,
  MoreVertical,
  Shield,
  ShieldCheck,
  ShieldOff,
  UserMinus,
  UserX,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Mail,
  CreditCard,
  Pencil,
  Layers,
  LayoutDashboard,
  Users,
  Building2,
  UserCheck,
  Package,
  BarChart3,
  PoundSterling,
  AppWindow,
  LucideIcon,
} from 'lucide-react';
import { useTeamList, useTeamOperations, type TeamMember, type TeamRole, type ModulePermissions, type ModuleId, type ModuleAccess } from '@hooks/useTeam';
import { sendEmail } from '@lib/msal';
import { resolveModuleAccess } from '@utils/permissions';
import { LINKED_APPS, APP_ROLE_LABELS, APP_ROLE_DESCRIPTIONS, type AppAccess, type AppId, type AppRole } from '@lib/linkedApps';

type ModalType = 'invite' | 'editRole' | 'editDebitCard' | 'deactivate' | 'remove' | 'resendEmail' | 'modulePermissions' | 'appAccess' | null;

// Module definitions for the permissions editor
const MODULE_DEFINITIONS: { id: ModuleId; name: string; icon: LucideIcon; allowNone: boolean }[] = [
  { id: 'dashboard',     name: 'Dashboard',     icon: LayoutDashboard, allowNone: false },
  { id: 'people',        name: 'People',        icon: Users,           allowNone: true  },
  { id: 'organisations', name: 'Organisations', icon: Building2,       allowNone: true  },
  { id: 'volunteers',    name: 'Volunteers',    icon: UserCheck,       allowNone: true  },
  { id: 'assets',        name: 'Assets',        icon: Package,         allowNone: true  },
  { id: 'reports',       name: 'Reports',       icon: BarChart3,       allowNone: true  },
  { id: 'finance',       name: 'Finance',       icon: PoundSterling,   allowNone: true  },
];

export default function TeamManagement() {
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username?.toLowerCase() || '';

  const { data: members, isLoading, error, refetch } = useTeamList();
  const { invite, updateRole, updateStatus, updateDebitCard, updateModulePermissions, updateAppAccess, remove, isLoading: isOperating } = useTeamOperations();

  const [searchQuery, setSearchQuery] = useState('');
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<TeamRole>('Regular');
  const [sendInviteEmail, setSendInviteEmail] = useState(true);

  // Edit role state
  const [newRole, setNewRole] = useState<TeamRole>('Regular');

  // Edit debit card state
  const [debitCardValue, setDebitCardValue] = useState('');

  // Feedback state
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Email sending state
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Module permissions state
  const [draftPermissions, setDraftPermissions] = useState<ModulePermissions>({});

  // App access state
  const [draftAppAccess, setDraftAppAccess] = useState<AppAccess>({});

  // Filter members by search
  const filteredMembers = members?.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => { setActionMenuOpen(null); setMenuPos(null); };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Auto-hide feedback after 5 seconds
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const showFeedback = (type: 'success' | 'error', message: string) => {
    setFeedback({ type, message });
  };

  const handleInvite = async () => {
    try {
      await invite({ email: inviteEmail, name: inviteName, role: inviteRole });

      // Send invitation email if checkbox is checked
      if (sendInviteEmail) {
        const inviterName = accounts[0]?.name || 'An administrator';
        const roleDescription = inviteRole === 'Admin'
          ? 'full administrative access'
          : inviteRole === 'Regular'
            ? 'standard access to view and edit data'
            : 'read-only access to view data';

        const emailHtml = `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <div style="background: linear-gradient(135deg, #7c3aed 0%, #db2777 100%); padding: 30px; text-align: center;">
              <h1 style="color: white; margin: 0;">SCA UK Operations Hub</h1>
            </div>
            <div style="padding: 30px; background: #f9fafb;">
              <p style="font-size: 16px; color: #374151;">Hello ${inviteName},</p>
              <p style="font-size: 16px; color: #374151;">
                ${inviterName} has invited you to join the <strong>SCA UK Operations Hub</strong> -
                our internal system for managing volunteers, resources, and operations.
              </p>
              <p style="font-size: 16px; color: #374151;">
                You have been granted <strong>${roleDescription}</strong>.
              </p>
              <div style="text-align: center; margin: 30px 0;">
                <a href="https://hub.scauk.org"
                   style="background: #7c3aed; color: white; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold;">
                  Access the Operations Hub
                </a>
              </div>
              <p style="font-size: 14px; color: #6b7280;">
                Simply click the button above and sign in with your Microsoft account (${inviteEmail}).
              </p>
              <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;">
              <p style="font-size: 12px; color: #9ca3af; text-align: center;">
                Sudden Cardiac Arrest UK<br>
                Registered Charity No. 1159656
              </p>
            </div>
          </div>
        `;

        const emailSent = await sendEmail(
          inviteEmail,
          'You\'ve been invited to SCA UK Operations Hub',
          emailHtml
        );

        if (emailSent) {
          showFeedback('success', `Invited ${inviteName} and sent email notification`);
        } else {
          showFeedback('success', `Invited ${inviteName} (email notification could not be sent)`);
        }
      } else {
        showFeedback('success', `Invited ${inviteName} to the team`);
      }

      setModalType(null);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('Regular');
      setSendInviteEmail(true);
      refetch();
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to invite user');
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedMember) return;
    try {
      await updateRole(selectedMember.email, newRole);
      showFeedback('success', `Updated ${selectedMember.name}'s role to ${newRole}`);
      setModalType(null);
      setSelectedMember(null);
      refetch();
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedMember) return;
    const newStatus = selectedMember.status === 'Active' ? 'Deactivated' : 'Active';
    try {
      await updateStatus(selectedMember.email, newStatus);
      showFeedback('success', `${selectedMember.name} has been ${newStatus === 'Active' ? 'reactivated' : 'deactivated'}`);
      setModalType(null);
      setSelectedMember(null);
      refetch();
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handleRemove = async () => {
    if (!selectedMember) return;
    try {
      await remove(selectedMember.email);
      showFeedback('success', `Removed ${selectedMember.name} from the team`);
      setModalType(null);
      setSelectedMember(null);
      refetch();
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  const handleResendEmail = async () => {
    if (!selectedMember) return;
    setIsSendingEmail(true);
    try {
      const inviterName = accounts[0]?.name || 'An administrator';
      const roleDescription = selectedMember.role === 'Admin'
        ? 'full administrative access'
        : selectedMember.role === 'Regular'
          ? 'standard access to view and edit data'
          : 'read-only access to view data';

      const emailHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #7c3aed 0%, #db2777 100%); padding: 30px; text-align: center;">
            <h1 style="color: white; margin: 0;">SCA UK Operations Hub</h1>
          </div>
          <div style="padding: 30px; background: #f9fafb;">
            <p style="font-size: 16px; color: #374151;">Hello ${selectedMember.name},</p>
            <p style="font-size: 16px; color: #374151;">
              This is a reminder that ${inviterName} has invited you to join the <strong>SCA UK Operations Hub</strong> -
              our internal system for managing volunteers, resources, and operations.
            </p>
            <p style="font-size: 16px; color: #374151;">
              You have been granted <strong>${roleDescription}</strong>.
            </p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="https://hub.scauk.org"
                 style="background: #7c3aed; color: white; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold;">
                Access the Operations Hub
              </a>
            </div>
            <p style="font-size: 14px; color: #6b7280;">
              Simply click the button above and sign in with your Microsoft account (${selectedMember.email}).
            </p>
            <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;">
            <p style="font-size: 12px; color: #9ca3af; text-align: center;">
              Sudden Cardiac Arrest UK<br>
              Registered Charity No. 1159656
            </p>
          </div>
        </div>
      `;

      const emailSent = await sendEmail(
        selectedMember.email,
        'Reminder: You\'ve been invited to SCA UK Operations Hub',
        emailHtml
      );

      if (emailSent) {
        showFeedback('success', `Invitation email resent to ${selectedMember.name}`);
      } else {
        showFeedback('error', 'Failed to send email. Please try again or check your permissions.');
      }

      setModalType(null);
      setSelectedMember(null);
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to resend email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const openResendEmail = (member: TeamMember) => {
    setSelectedMember(member);
    setModalType('resendEmail');
    setActionMenuOpen(null);
  };

  const openEditRole = (member: TeamMember) => {
    setSelectedMember(member);
    setNewRole(member.role);
    setModalType('editRole');
    setActionMenuOpen(null);
  };

  const openEditDebitCard = (member: TeamMember) => {
    setSelectedMember(member);
    setDebitCardValue(member.debitCardIdentifier || '');
    setModalType('editDebitCard');
    setActionMenuOpen(null);
  };

  const handleUpdateDebitCard = async () => {
    if (!selectedMember) return;
    try {
      await updateDebitCard(selectedMember.email, debitCardValue);
      const msg = debitCardValue
        ? `Debit card identifier updated for ${selectedMember.name}`
        : `Debit card identifier cleared for ${selectedMember.name}`;
      showFeedback('success', msg);
      setModalType(null);
      setSelectedMember(null);
      refetch();
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to update debit card identifier');
    }
  };

  const openDeactivate = (member: TeamMember) => {
    setSelectedMember(member);
    setModalType('deactivate');
    setActionMenuOpen(null);
  };

  const openRemove = (member: TeamMember) => {
    setSelectedMember(member);
    setModalType('remove');
    setActionMenuOpen(null);
  };

  const openModulePermissions = (member: TeamMember) => {
    setSelectedMember(member);
    setDraftPermissions(member.modulePermissions ?? {});
    setModalType('modulePermissions');
    setActionMenuOpen(null);
  };

  const handleUpdateModulePermissions = async () => {
    if (!selectedMember) return;
    try {
      // Send null to clear all overrides when draft is empty
      const toSave = Object.keys(draftPermissions).length === 0 ? null : draftPermissions;
      await updateModulePermissions(selectedMember.email, toSave);
      showFeedback('success', `Updated module access for ${selectedMember.name}`);
      setModalType(null);
      setSelectedMember(null);
      setDraftPermissions({});
      refetch();
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to update module access');
    }
  };

  const openAppAccess = (member: TeamMember) => {
    setSelectedMember(member);
    setDraftAppAccess(member.appAccess ?? {});
    setModalType('appAccess');
    setActionMenuOpen(null);
  };

  const handleUpdateAppAccess = async () => {
    if (!selectedMember) return;
    try {
      // Send null to remove all app access when draft is empty
      const toSave = Object.keys(draftAppAccess).length === 0 ? null : draftAppAccess;
      await updateAppAccess(selectedMember.email, toSave);
      showFeedback('success', `Updated app access for ${selectedMember.name}`);
      setModalType(null);
      setSelectedMember(null);
      setDraftAppAccess({});
      refetch();
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to update app access');
    }
  };

  const setAppRole = (appId: AppId, role: AppRole | 'none') => {
    setDraftAppAccess((prev) => {
      const next = { ...prev };
      if (role === 'none') {
        delete next[appId];
      } else {
        next[appId] = role;
      }
      return next;
    });
  };

  const setModuleAccess = (moduleId: ModuleId, access: ModuleAccess | 'default') => {
    setDraftPermissions((prev) => {
      const next = { ...prev };
      if (access === 'default') {
        delete next[moduleId];
      } else {
        next[moduleId] = access;
      }
      return next;
    });
  };

  const getRoleBadgeStyles = (role: TeamRole) => {
    switch (role) {
      case 'Admin':
        return 'bg-purple-100 text-purple-700';
      case 'Regular':
        return 'bg-blue-100 text-blue-700';
      case 'ReadOnly':
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusBadgeStyles = (status: string) => {
    return status === 'Active'
      ? 'bg-green-100 text-green-700'
      : 'bg-red-100 text-red-700';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage who has access to the Operations Hub
          </p>
        </div>
        <button
          onClick={() => setModalType('invite')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-sca-purple-500 text-white rounded-lg hover:bg-sca-purple-600 transition-colors"
        >
          <UserPlus className="h-4 w-4" />
          Invite User
        </button>
      </div>

      {/* Feedback banner */}
      {feedback && (
        <div className={`flex items-center gap-2 p-4 rounded-lg ${
          feedback.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {feedback.type === 'success' ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* Search and refresh */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sca-purple-500 focus:border-sca-purple-500"
          />
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg border border-red-200">
          <p className="font-medium">Failed to load team members</p>
          <p className="text-sm mt-1">{error.message}</p>
        </div>
      )}

      {/* Loading state */}
      {isLoading && !members && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-sca-purple-500" />
        </div>
      )}

      {/* Team members table */}
      {members && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Debit Card
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invited
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      {searchQuery ? 'No members match your search' : 'No team members yet'}
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((member) => {
                    const isCurrentUser = member.email.toLowerCase() === currentUserEmail;
                    return (
                      <tr key={member.id} className={isCurrentUser ? 'bg-purple-50/50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {member.name}
                              </span>
                              {isCurrentUser && (
                                <span className="text-xs text-sca-purple-600 font-medium">(You)</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{member.email}</div>
                            {member.modulePermissions && Object.keys(member.modulePermissions).length > 0 && (
                              <button
                                onClick={() => openModulePermissions(member)}
                                className="mt-0.5 inline-flex items-center gap-1 text-xs text-sca-purple-600 hover:text-sca-purple-800"
                                title="Module access configured"
                              >
                                <Layers className="h-3 w-3" />
                                {Object.keys(member.modulePermissions).length} module override{Object.keys(member.modulePermissions).length !== 1 ? 's' : ''}
                              </button>
                            )}
                            {member.appAccess && Object.keys(member.appAccess).length > 0 && (
                              <button
                                onClick={() => openAppAccess(member)}
                                className="mt-0.5 ml-2 inline-flex items-center gap-1 text-xs text-sca-purple-600 hover:text-sca-purple-800"
                                title="App access configured"
                              >
                                <AppWindow className="h-3 w-3" />
                                {Object.keys(member.appAccess).length} app{Object.keys(member.appAccess).length !== 1 ? 's' : ''}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeStyles(member.role)}`}>
                            {member.role === 'Admin' && <ShieldCheck className="h-3 w-3" />}
                            {member.role === 'Regular' && <Shield className="h-3 w-3" />}
                            {member.role === 'ReadOnly' && <ShieldOff className="h-3 w-3" />}
                            {member.role === 'ReadOnly' ? 'Read-only' : member.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeStyles(member.status)}`}>
                            {member.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            {member.debitCardIdentifier ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-700">
                                <CreditCard className="h-3 w-3" />
                                ••••{member.debitCardIdentifier}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                            <button
                              onClick={() => openEditDebitCard(member)}
                              className="p-1 text-gray-400 hover:text-sca-purple-600 hover:bg-purple-50 rounded transition-colors"
                              title="Edit debit card"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(member.invitedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (actionMenuOpen === member.id) {
                                setActionMenuOpen(null);
                                setMenuPos(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setMenuPos({
                                  top: rect.bottom + 4,
                                  right: window.innerWidth - rect.right,
                                });
                                setActionMenuOpen(member.id);
                              }
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          >
                            <MoreVertical className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action dropdown — rendered at fixed viewport position to escape overflow-hidden table container */}
      {actionMenuOpen && menuPos && (() => {
        const menuMember = filteredMembers.find(m => m.id === actionMenuOpen);
        if (!menuMember) return null;
        const isCurrentUser = menuMember.email.toLowerCase() === currentUserEmail;
        return (
          <div
            style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 60 }}
            className="w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => openResendEmail(menuMember)}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Mail className="h-4 w-4" />
              Resend Email
            </button>
            <button
              onClick={() => openEditRole(menuMember)}
              disabled={isCurrentUser}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Shield className="h-4 w-4" />
              Change Role
            </button>
            <button
              onClick={() => openModulePermissions(menuMember)}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Layers className="h-4 w-4" />
              Module Access
            </button>
            <button
              onClick={() => openAppAccess(menuMember)}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <AppWindow className="h-4 w-4" />
              App Access
            </button>
            <button
              onClick={() => openEditDebitCard(menuMember)}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <CreditCard className="h-4 w-4" />
              Edit Debit Card
            </button>
            <button
              onClick={() => openDeactivate(menuMember)}
              disabled={isCurrentUser}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserMinus className="h-4 w-4" />
              {menuMember.status === 'Active' ? 'Deactivate' : 'Reactivate'}
            </button>
            <hr className="my-1" />
            <button
              onClick={() => openRemove(menuMember)}
              disabled={isCurrentUser}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserX className="h-4 w-4" />
              Remove
            </button>
          </div>
        );
      })()}

      {/* Invite Modal */}
      {modalType === 'invite' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalType(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Invite User</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sca-purple-500 focus:border-sca-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sca-purple-500 focus:border-sca-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as TeamRole)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sca-purple-500 focus:border-sca-purple-500"
                >
                  <option value="Admin">Admin - Full access including team management</option>
                  <option value="Regular">Regular - Can view and edit data</option>
                  <option value="ReadOnly">Read-only - Can only view data</option>
                </select>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="sendInviteEmail"
                  checked={sendInviteEmail}
                  onChange={(e) => setSendInviteEmail(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-sca-purple-500 focus:ring-sca-purple-500"
                />
                <label htmlFor="sendInviteEmail" className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Mail className="h-4 w-4 text-gray-400" />
                  Send email notification with access instructions
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setModalType(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={!inviteEmail || !inviteName || isOperating}
                className="flex items-center gap-2 px-4 py-2 bg-sca-purple-500 text-white rounded-lg hover:bg-sca-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isOperating && <Loader2 className="h-4 w-4 animate-spin" />}
                Invite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {modalType === 'editRole' && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalType(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Change Role</h2>
            <p className="text-sm text-gray-500 mb-4">
              Update the role for <strong>{selectedMember.name}</strong>
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as TeamRole)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sca-purple-500 focus:border-sca-purple-500"
              >
                <option value="Admin">Admin - Full access including team management</option>
                <option value="Regular">Regular - Can view and edit data</option>
                <option value="ReadOnly">Read-only - Can only view data</option>
              </select>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setModalType(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRole}
                disabled={newRole === selectedMember.role || isOperating}
                className="flex items-center gap-2 px-4 py-2 bg-sca-purple-500 text-white rounded-lg hover:bg-sca-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isOperating && <Loader2 className="h-4 w-4 animate-spin" />}
                Update Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate/Reactivate Modal */}
      {modalType === 'deactivate' && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalType(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {selectedMember.status === 'Active' ? 'Deactivate' : 'Reactivate'} User
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {selectedMember.status === 'Active' ? (
                <>
                  Are you sure you want to deactivate <strong>{selectedMember.name}</strong>?
                  They will no longer be able to access the Operations Hub.
                </>
              ) : (
                <>
                  Are you sure you want to reactivate <strong>{selectedMember.name}</strong>?
                  They will be able to access the Operations Hub again.
                </>
              )}
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setModalType(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleToggleStatus}
                disabled={isOperating}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  selectedMember.status === 'Active'
                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {isOperating && <Loader2 className="h-4 w-4 animate-spin" />}
                {selectedMember.status === 'Active' ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Modal */}
      {modalType === 'remove' && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalType(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Remove User</h2>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to permanently remove <strong>{selectedMember.name}</strong> from the team?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setModalType(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRemove}
                disabled={isOperating}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isOperating && <Loader2 className="h-4 w-4 animate-spin" />}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Debit Card Modal */}
      {modalType === 'editDebitCard' && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalType(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Edit Debit Card Identifier</h2>
            <p className="text-sm text-gray-500 mb-4">
              For <strong>{selectedMember.name}</strong>. Used to match unapproved FreeAgent transactions.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last 4 digits of debit card
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={debitCardValue}
                  onChange={(e) => {
                    // Only allow digits, max 4
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setDebitCardValue(val);
                  }}
                  placeholder="e.g. 1234"
                  maxLength={4}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sca-purple-500 focus:border-sca-purple-500 font-mono tracking-widest"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Enter exactly 4 digits, or leave blank to clear.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setModalType(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateDebitCard}
                disabled={(debitCardValue !== '' && debitCardValue.length !== 4) || isOperating}
                className="flex items-center gap-2 px-4 py-2 bg-sca-purple-500 text-white rounded-lg hover:bg-sca-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isOperating && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Module Permissions Modal */}
      {modalType === 'modulePermissions' && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalType(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Module Access</h2>
            <p className="text-sm text-gray-600 mb-1">
              Configuring access for <strong>{selectedMember.name}</strong>
              <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                selectedMember.role === 'Admin' ? 'bg-purple-100 text-purple-700' :
                selectedMember.role === 'Regular' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {selectedMember.role === 'ReadOnly' ? 'Read-only' : selectedMember.role}
              </span>
            </p>
            <p className="text-xs text-gray-400 mb-5">
              Use <strong>Default</strong> to inherit from their role ({selectedMember.role === 'ReadOnly' ? 'Read on all modules' : 'Edit on all modules'}). Override individual modules below.
            </p>

            <div className="divide-y divide-gray-100">
              {MODULE_DEFINITIONS.map(({ id, name, icon: Icon, allowNone }) => {
                const stored = draftPermissions[id];
                const effective = stored ?? 'default';
                const levels: Array<ModuleAccess | 'default'> = ['edit', 'read', ...(allowNone ? ['none' as ModuleAccess] : []), 'default'];

                return (
                  <div key={id} className="flex items-center gap-3 py-2.5">
                    <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="flex-1 text-sm font-medium text-gray-700">{name}</span>
                    <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
                      {levels.map((level) => {
                        const isActive = effective === level;
                        const label = level === 'default'
                          ? `Default (${selectedMember.role === 'ReadOnly' ? 'Read' : 'Edit'})`
                          : level.charAt(0).toUpperCase() + level.slice(1);
                        const activeClass = level === 'none'
                          ? 'bg-red-500 text-white'
                          : level === 'read'
                            ? 'bg-amber-500 text-white'
                            : level === 'default'
                              ? 'bg-gray-200 text-gray-700'
                              : 'bg-sca-purple-500 text-white';
                        return (
                          <button
                            key={level}
                            onClick={() => setModuleAccess(id, level)}
                            className={`px-2.5 py-1.5 transition-colors border-l border-gray-200 first:border-l-0 ${
                              isActive ? activeClass : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Preview of effective access */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-medium text-gray-500 mb-2">Effective access preview</p>
              <div className="flex flex-wrap gap-2">
                {MODULE_DEFINITIONS.map(({ id, name }) => {
                  const access = resolveModuleAccess(id, selectedMember.role, draftPermissions);
                  const colour = access === 'none' ? 'bg-red-100 text-red-700' :
                    access === 'read' ? 'bg-amber-100 text-amber-700' :
                    'bg-green-100 text-green-700';
                  return (
                    <span key={id} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colour}`}>
                      {name}: {access}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between items-center mt-5">
              <button
                onClick={() => setDraftPermissions({})}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Reset all to default
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => { setModalType(null); setDraftPermissions({}); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateModulePermissions}
                  disabled={isOperating}
                  className="flex items-center gap-2 px-4 py-2 bg-sca-purple-500 text-white rounded-lg hover:bg-sca-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isOperating && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* App Access Modal */}
      {modalType === 'appAccess' && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalType(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">App Access</h2>
            <p className="text-sm text-gray-600 mb-1">
              Configuring linked app access for <strong>{selectedMember.name}</strong>
            </p>
            <p className="text-xs text-gray-400 mb-5">
              Roles here control what this member can see and do inside each linked app.
              <strong> No access</strong> hides the app from their dashboard and blocks its API.
            </p>

            <div className="divide-y divide-gray-100">
              {LINKED_APPS.map((linkedApp) => {
                const current = draftAppAccess[linkedApp.id] ?? 'none';
                const options: Array<AppRole | 'none'> = [...linkedApp.roles, 'none'];

                return (
                  <div key={linkedApp.id} className="py-3">
                    <div className="flex items-center gap-3">
                      <AppWindow className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700">{linkedApp.name}</p>
                        <p className="text-xs text-gray-400 truncate">{linkedApp.description}</p>
                      </div>
                      <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
                        {options.map((option) => {
                          const isActive = current === option;
                          const label = option === 'none' ? 'No access' : APP_ROLE_LABELS[option];
                          const activeClass = option === 'none'
                            ? 'bg-gray-200 text-gray-700'
                            : option === 'readonly'
                              ? 'bg-amber-500 text-white'
                              : option === 'scorer'
                                ? 'bg-blue-500 text-white'
                                : 'bg-sca-purple-500 text-white';
                          return (
                            <button
                              key={option}
                              onClick={() => setAppRole(linkedApp.id, option)}
                              title={option === 'none' ? 'No access to this app' : APP_ROLE_DESCRIPTIONS[option]}
                              className={`px-2.5 py-1.5 transition-colors border-l border-gray-200 first:border-l-0 ${
                                isActive ? activeClass : 'bg-white text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between items-center mt-5">
              <button
                onClick={() => setDraftAppAccess({})}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Remove all app access
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => { setModalType(null); setDraftAppAccess({}); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateAppAccess}
                  disabled={isOperating}
                  className="flex items-center gap-2 px-4 py-2 bg-sca-purple-500 text-white rounded-lg hover:bg-sca-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isOperating && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resend Email Modal */}
      {modalType === 'resendEmail' && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalType(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Resend Invitation Email</h2>
            <p className="text-sm text-gray-600 mb-4">
              Send another invitation email to <strong>{selectedMember.name}</strong> ({selectedMember.email})?
            </p>
            <p className="text-sm text-gray-500 mb-4">
              The email will include instructions on how to access the Operations Hub and their current role ({selectedMember.role === 'ReadOnly' ? 'Read-only' : selectedMember.role}).
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setModalType(null)}
                disabled={isSendingEmail}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResendEmail}
                disabled={isSendingEmail}
                className="flex items-center gap-2 px-4 py-2 bg-sca-purple-500 text-white rounded-lg hover:bg-sca-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {isSendingEmail ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
